/**
 * 💰 DRIVER PRICING BOUNDARIES SERVICE
 * Manages admin configuration for driver pricing min/max boundaries
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


export interface DriverPricingBoundaries {
  hourlyMin: number;
  hourlyMax: number;
  dailyMin: number;
  dailyMax: number;
  currency: string; // ISO 4217 code: NGN, KES, GHS, ZAR, USD, EUR, GBP
  updatedAt?: string;
  updatedBy?: string;
}

export interface DriverPricingResponse {
  success: boolean;
  data?: DriverPricingBoundaries;
  message?: string;
  error?: string;
}

export const driverPricingService = {
  /**
   * 📥 Get current driver pricing boundaries
   */
  async getBoundaries(): Promise<DriverPricingResponse> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/settings/driver-pricing`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // If endpoint not yet implemented, return default values
        if (response.status === 404) {
          return {
            success: true,
            data: {
              hourlyMin: 1000,
              hourlyMax: 10000,
              dailyMin: 15000,
              dailyMax: 100000,
              currency: 'NGN'
            }
          };
        }

        const errorText = await response.text();
        console.error(`❌ Get Driver Pricing API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Backend returns nested structure: { hourly: { min, max }, daily: { min, max } }
      // Transform to flat structure for frontend
      if (result.success && result.data) {
        const backendData = result.data;
        return {
          success: true,
          data: {
            hourlyMin: backendData.hourly?.min || 1000,
            hourlyMax: backendData.hourly?.max || 10000,
            dailyMin: backendData.daily?.min || 15000,
            dailyMax: backendData.daily?.max || 100000,
            currency: backendData.currency || 'NGN'
          }
        };
      }

      return result;
    } catch (error) {
      console.error('❌ Error fetching driver pricing boundaries:', error);

      // Return default values on error
      return {
        success: true,
        data: {
          hourlyMin: 1000,
          hourlyMax: 10000,
          dailyMin: 15000,
          dailyMax: 100000,
          currency: 'NGN'
        }
      };
    }
  },

  /**
   * 💾 Update driver pricing boundaries
   */
  async updateBoundaries(boundaries: {
    hourlyMin: number;
    hourlyMax: number;
    dailyMin: number;
    dailyMax: number;
    currency?: string;
  }): Promise<DriverPricingResponse> {
    try {
      const url = `${getApiBaseUrl()}/api/v1/admin/settings/driver-pricing`;

      // Get admin user ID from localStorage
      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const adminUserId = getAdminUserId();

      // Backend expects nested structure: { hourly: { min, max }, daily: { min, max } }
      const requestBody = {
        hourly: {
          min: boundaries.hourlyMin,
          max: boundaries.hourlyMax
        },
        daily: {
          min: boundaries.dailyMin,
          max: boundaries.dailyMax
        },
        currency: boundaries.currency || 'NGN',
        adminUserId
      };

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Driver Pricing API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update driver pricing (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: result.success !== undefined ? result.success : true,
        message: result.message || 'Driver pricing boundaries updated successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error updating driver pricing boundaries:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update driver pricing'
      };
    }
  }
};
